import { Component, Input, Output, EventEmitter, OnChanges, SimpleChanges, OnInit, OnDestroy } from '@angular/core';
import { Subject } from 'rxjs';
import { debounceTime, takeUntil, filter, take } from 'rxjs/operators';
import { ThreeDotsMenuItem } from '@core/components/helpers/navigation/three-dots-menu/three-dots-menu-item';

import { AmsFile } from '@core/objects/ams-file';
import { User } from '@core/objects/user';
import { AnnotationsCounter } from '@core/objects/annotations';
import { AmsFilesListItemMenuClick, SIGNATURE_DEPENDENT_MENU_ACTIONS } from '@core/components/files/lists/files-list-item-menu-click';
import { FileSignatureManagerService } from '@core/services/files/file-signature-manager.service';
import { CoreFileSignatureService } from '@core/services/files/core-file-signature.service';
import { PendingActionsService } from '@core/services/files/pending-actions.service';
import * as flContexts from '@core/constants/files-list-contexts.constants';


/**
 * SignedFilesListComponent
 * 
 * Enhanced version of FilesListNotesComponent with signature validation.
 * 
 * Key Features:
 * - Displays files with both list and grid view modes
 * - Validates file signatures before performing actions
 * - Automatically refreshes expired/expiring signatures
 * - Supports notes counters and annotations for files when annotationsUser is provided
 * 
 * Signature Lifecycle:
 * 1. User clicks action (preview, download, etc.)
 * 2. Check if signature is EXPIRED:
 *    - YES: Block action, emit refreshSignaturesNeeded, wait for new files
 *    - NO: Proceed with action
 * 3. After action, check if signature is EXPIRING SOON (within 20 seconds):
 *    - YES: Emit refreshSignaturesNeeded (non-blocking, refresh after action)
 *    - NO: Do nothing, user will trigger next action later
 * 
 * Parent component should:
 * - Listen to refreshSignaturesNeeded output
 * - Reload the files list from API
 * - Update @Input files with fresh signatures
 * - Component automatically executes pending action on ngOnChanges
 */
@Component({
    selector: 'core-signed-files-list',
    templateUrl: './signed-files-list.component.html',
    styleUrls: ['./signed-files-list.component.scss']
})
export class SignedFilesListComponent implements OnInit, OnChanges, OnDestroy {
    public readonly STATUS_LOADING = 0;
    public readonly STATUS_DONE = 1;

    // GRID MANAGEMENT: 
    // Initial number of items to load at once
    public readonly GRID_ITEMS_PER_PAGE = 12;

    public readonly FILES_VIEW_MODE_LIST = 'list';
    public readonly FILES_VIEW_MODE_GRID = 'grid';

    @Input() files: Array<AmsFile>;
    // this is the selected file in the list for preview coming from parent, only used to UNSET fileselected.
    @Input() file: AmsFile;
    @Input() annotationsUser: User = null;
    @Input() scope: string = flContexts.CTX.DEFAULT;
    // may-2023: notes will become the notes counter
    @Input() notes: Array<AnnotationsCounter>;
    @Input() menu: Array<ThreeDotsMenuItem> = [];
    @Input() annotationsUrl: string = '';
    @Input() canCreateNotes: boolean = false;
    /**
     * Flag from parent indicating if signature refresh is currently ongoing
     * When true: disables all file actions and shows loading state
     * Prevents user interactions during API calls and avoid UI loops
     */
    @Input() isRefreshing: boolean = false;

    @Output() actionClick = new EventEmitter<AmsFile>();
    @Output() menuClick = new EventEmitter<AmsFilesListItemMenuClick>();
    @Output() iconDownloadClick = new EventEmitter<AmsFile>();
    @Output() annotationsSaved = new EventEmitter<void>();
    /**
     * Emitted when signature refresh is needed
     * Parent should reload file list from API
     */
    @Output() refreshSignaturesNeeded = new EventEmitter<void>();

    public status: number = this.STATUS_LOADING;
    public completeMenu: Array<ThreeDotsMenuItem> = [];
    public menuWithComments: Array<ThreeDotsMenuItem> = [];
    public menuWithSignatures: Array<ThreeDotsMenuItem> = [];
    public viewMode: string = this.FILES_VIEW_MODE_LIST;

    public displayedFiles: Array<AmsFile> = [];
    public fileSelected: AmsFile = null;

    // Pending action state
    private pendingMenuClick: AmsFilesListItemMenuClick | null = null;
    private pendingFileId: string | null = null;
    private shouldTriggerRefreshAfterAction: boolean = false;

    // Track files pending refresh for UI feedback
    public filesPendingRefresh = new Set<string>();

    // Refresh origin tracking
    // Set to TRUE when THIS component triggers refresh (onMenuClick, openPreview, etc.)
    // Set to FALSE when parent updates files array (new signatures from API)
    // Used to determine if we should update displayedFiles or only signature cache
    private isRefreshInitiatedByThis: boolean = false;

    // Store the view mode before refresh so we can restore it after
    private viewModeBeforeRefresh: string = this.FILES_VIEW_MODE_LIST;

    // Signature cache - maintains AmsFileAccess independently of files array
    // This allows updating signatures without causing UI re-renders
    private fileSignaturesMap = new Map<string, any>(); // AmsFileAccess stored by file ID

    // Debounced refresh state - consolidates multiple rapid refresh requests
    private refreshNeeded$ = new Subject<void>();
    private destroy$ = new Subject<void>();

    // CAREFUL: click management has to be controlled in parent component by key 
    // or trigger here for all components a notification to download. But here we dont have prefixUrl and/or scope
    public itemMenuWithComments: ThreeDotsMenuItem = {
        title: "Download with comments",
        key: "download-comments",
        icon: "download_comments",
        order: 5
    };

    public itemMenuPdfWithSignatures: ThreeDotsMenuItem = {
        title: "Download with signatures",
        key: "download-signatures",
        icon: "download_comments",
        order: 6
    };

    constructor(
        private signatureManager: FileSignatureManagerService,
        private coreFileSignatureService: CoreFileSignatureService,
        private pendingActionsSrv: PendingActionsService
    ) {}

    ngOnInit(): void {
        this.setMenus();
        this.initGridItems();

        // Setup debounced refresh emission
        // Consolidates multiple rapid refresh requests (e.g., 7 files failing at once) into single emission
        this.refreshNeeded$
            .pipe(
                debounceTime(100), // Wait 100ms for request silence before emitting
                takeUntil(this.destroy$)
            )
            .subscribe(() => {
                this.refreshSignaturesNeeded.emit();
            });

        // Subscribe to signature broadcasts from Core service
        // When any component refreshes signatures, update our local cache
        // This ensures signatures are fresh across all lists before user interacts
        this.coreFileSignatureService.getSignatureUpdates$()
            .pipe(takeUntil(this.destroy$))
            .subscribe(({ fileId, access }) => {
                this.fileSignaturesMap.set(fileId, access);
            });
    }

    ngOnChanges(changes: SimpleChanges): void {

        if (changes.notes && changes.notes.currentValue) {
            this.status = this.STATUS_DONE;
        }

        if (changes.file && this.file == null) {
            // set/unset current selected file to null to quit highlighted 'on-preview' class on the list item
            this.fileSelected = null;
        }

        // Handle files input change
        if (changes.files && changes.files.currentValue) {
            const updatedFiles = changes.files.currentValue;

            if (this.fileSelected != null) {
                const file = this.files.find((f: AmsFile) => f.uuid === this.fileSelected.uuid);
                if (file) {
                    this.fileSelected = file;
                }
            }
            // STEP 1: Always extract and cache signatures from new files
            // This way, signature-dependent actions (download, preview) have fresh signatures
            this.cacheSignaturesFromFiles(updatedFiles);

            // STEP 1.4: Check if THIS component should restore pending action
            // When messagesList is rebuilt and core-signed-files-list is recreated,
            // only restore the pending action if this component is displaying the file that has the pending action
            if (!this.pendingMenuClick && !this.pendingFileId) {
                const restoredPending = this.pendingActionsSrv.getPendingAction();
                if (restoredPending) {
                    // Check if the pending file is in our files array
                    const pendingFileExists = updatedFiles.find((f: AmsFile) => f.id === restoredPending.fileId);
                    if (pendingFileExists) {
                        this.pendingMenuClick = restoredPending.action;
                        this.pendingFileId = restoredPending.fileId;
                        this.filesPendingRefresh.add(this.pendingFileId);
                    } else {
                        // console.log('$$$ [SignedFilesListComponent] Pending action exists but file not in this component - fileId:', restoredPending.fileId, 'files in this component:', updatedFiles?.map((f: AmsFile) => f.id));
                    }
                }
            }

            // STEP 1.5: Check if we have a pending action waiting for signature refresh
            // When user clicks menu/download with expired signature, action is queued as pendingMenuClick
            // Once signatures refresh and arrive via ngOnChanges, execute the pending action here
            if (this.pendingMenuClick && this.pendingFileId) {
                const pendingFile = updatedFiles.find((f: AmsFile) => f.id === this.pendingFileId);

                if (pendingFile) {
                    // Check if signature is now valid
                    if (!this.signatureManager.isSignatureExpired(pendingFile)) {
                        // IMPORTANT: Create NEW action object with FRESH file (not the stale stored one)
                        // The stored pendingMenuClick has the old file with expired signature
                        // We need to emit the updated file with fresh signature from API
                        const updatedAction: AmsFilesListItemMenuClick = {
                            ...this.pendingMenuClick,
                            file: pendingFile  // Use the fresh file with new signature
                        };

                        // Execute the pending action with fresh file
                        if (updatedAction.key === 'preview') {
                            this.fileSelected = pendingFile;
                            this.actionClick.emit(pendingFile);
                        } else {
                            this.menuClick.emit(updatedAction);
                        }

                        // Clear pending state from BOTH component and service
                        this.pendingMenuClick = null;
                        this.pendingFileId = null;
                        this.filesPendingRefresh.clear();
                        this.pendingActionsSrv.clearPendingAction();
                    } else {
                        // Still expired, mark as pending for UI feedback
                        this.filesPendingRefresh.add(this.pendingFileId);
                    }
                } else {
                    // File not found
                }
            }

            // STEP 2: Handle our own refresh - restore UI state
            // Note: Pending action execution is now handled in STEP 1.5 above when ngOnChanges receives files
            if (this.isRefreshInitiatedByThis) {
                // Clear pending refresh tracking
                this.filesPendingRefresh.clear();

                // Restore the view mode to what it was before refresh
                // This ensures user stays in list/grid view they were in
                this.viewMode = this.viewModeBeforeRefresh;
                
                // If we're in grid mode, re-initialize grid items with new files
                if (this.viewMode === this.FILES_VIEW_MODE_GRID) {
                    this.initGridItems();
                }

                // Reset flag for next cycle
                this.isRefreshInitiatedByThis = false;

            } else {
                // This is an external refresh (parent reloaded from elsewhere)
                // Only update signature cache, do NOT re-render files
                // IMPORTANT: Keep current viewMode (list/grid) and displayedFiles intact
                // This prevents UI loops and unnecessary re-renders
            }
        }
    }

    public getNotes(file: AmsFile) {
        return this.notes.filter((n: AnnotationsCounter) => n.file.id == file.id)[0]?.annotations?.count;
    }

    /**
     * Check if the given file is a PDF based on name or mimetype
     */
    public isPdfFile(file: AmsFile): boolean {
        // Check by mimetype first if available
        if (file.mimetype && file.mimetype.toLowerCase() === 'application/pdf') {
            return true;
        }
        // Check by file extension from the name
        if (file.name && file.name.toLowerCase().endsWith('.pdf')) {
            return true;
        }
        return false;
    }

    // here we select the menu we have to display depending on scope
    public getItemFileMenu(file: AmsFile): Array<ThreeDotsMenuItem> {
        switch (this.scope) {
            case flContexts.CTX.DEFAULT:
                // ctx default with notes
                if (this.getNotes(file) > 0)
                    return this.menuWithComments;
                return this.menu;
            case flContexts.CTX.APP_ARTWORK_DETAILS_FEEDBACK:
            case flContexts.CTX.SHARE_ARTWORK_DETAILS_FEEDBACK:
                if (this.getNotes(file) > 0 && this.isPdfFile(file))
                    return this.completeMenu;
                if (this.isPdfFile(file))
                    return this.menuWithSignatures;
                if (this.getNotes(file) > 0)
                    return this.menuWithComments;
                return this.menu;
            case flContexts.CTX.APP_ARTWORK_DETAILS:
                // ctx app artwork_details with notes
                if (this.getNotes(file) > 0)
                    return this.menuWithComments;
                return this.menu;
            default:
                return this.menu;
        }
    }

    public changeView(mode: string): void {
        // Prevent view changes while refresh is in progress
        if (this.isRefreshing) {
            return;
        }

        this.viewMode = mode;
        if (mode == this.FILES_VIEW_MODE_GRID) {
            this.initGridItems();
            // Check if files have expired signatures before showing grid
            this.checkFilesForExpiredSignatures();
        }
    }

    public showMoreGridItems(): void {
        // Prevent pagination while refresh is in progress
        if (this.isRefreshing) {
            return;
        }

        const startIndex = this.displayedFiles.length;
        const endIndex = startIndex + this.GRID_ITEMS_PER_PAGE;
        this.displayedFiles.push(...this.files.slice(startIndex, endIndex));
        
        // Check files for expired signatures
        this.checkFilesForExpiredSignatures();
    }

    /**
     * Handle preview clicks with signature validation
     *
     * Flow mirrors onIconDownloadClick/onMenuClick:
     * 1. Check if refresh is in progress - if so, block all actions
     * 2. Check if signature of clicked file is EXPIRED (blocking)
     *    - YES: Queue refresh BEFORE emitting actionClick to the parent
     * 3. Toggle selection and emit actionClick
     * 4. Check if signature is EXPIRING SOON (non-blocking)
     *    - YES: Queue refresh AFTER action
     */
    public openPreview(file: AmsFile): void {
        // STEP 0: Check if refresh is in progress
        if (this.isRefreshing) {
            return;
        }

        // STEP 1: Check if signature is EXPIRED (blocking) - preview needs a fresh signature too
        if (this.signatureManager.isSignatureExpired(file)) {
            // Save current view mode before initiating refresh
            this.viewModeBeforeRefresh = this.viewMode;
            this.isRefreshInitiatedByThis = true;

            // Store the action to execute after refresh
            this.pendingMenuClick = {
                key: 'preview',
                file: file
            };
            this.pendingFileId = file.id;
            this.filesPendingRefresh.add(file.id);

            // IMPORTANT: Also save to service so it survives component destruction
            this.pendingActionsSrv.setPendingAction(file.id, this.pendingMenuClick);

            // Setup listener for THIS file's signature refresh (preview is signature-dependent)
            this.setupPendingActionListener(this.pendingMenuClick, file.id);

            // Trigger debounced refresh - will consolidate multiple requests
            this.refreshNeeded$.next();
            // IMPORTANT: do NOT emit actionClick until the signature has been refreshed
            return;
        }

        // STEP 2: Signature is valid - toggle selection and emit action to parent
        if (this.fileSelected != null && this.fileSelected.id == file.id)
            this.fileSelected = null;
        else
            this.fileSelected = file;

        this.actionClick.emit(file);

        // STEP 3: Check if signature is EXPIRING SOON (non-blocking)
        // Trigger refresh after action so next action has fresh signature
        if (this.signatureManager.isSignatureExpiringSoon(file)) {
            // Save current view mode before initiating refresh
            this.viewModeBeforeRefresh = this.viewMode;
            this.isRefreshInitiatedByThis = true;
            this.refreshNeeded$.next();
        }
    }

    /**
     * Handle download icon clicks with signature validation
     * 
     * Flow:
     * 1. Check if refresh is in progress - if so, block all actions
     * 2. Check if signature of clicked file is EXPIRED (blocking)
     *    - YES: Queue refresh BEFORE action
     * 3. Execute download
     * 4. Check if signature is EXPIRING SOON (non-blocking)
     *    - YES: Queue refresh AFTER action
     */
    public onIconDownloadClick(file: AmsFile): void {
        // STEP 0: Check if refresh is in progress
        if (this.isRefreshing) {
            return;
        }
        // STEP 1: Check if signature is EXPIRED (blocking)
        if (this.signatureManager.isSignatureExpired(file)) {
            // Save current view mode before initiating refresh
            this.viewModeBeforeRefresh = this.viewMode;
            this.isRefreshInitiatedByThis = true;
            
            // Store the action to execute after refresh
            this.pendingMenuClick = {
                key: 'download',
                file: file
            };
            this.pendingFileId = file.id;
            this.filesPendingRefresh.add(file.id);
            
            // IMPORTANT: Also save to service so it survives component destruction
            this.pendingActionsSrv.setPendingAction(file.id, this.pendingMenuClick);
            
            // NEW: Setup listener for THIS file's signature refresh (download is signature-dependent)
            this.setupPendingActionListener(this.pendingMenuClick, file.id);
            
            // Trigger debounced refresh - will consolidate multiple requests
            this.refreshNeeded$.next();
            return;
        }

        // STEP 2: Signature is valid, execute download
        this.iconDownloadClick.emit(file);

        // STEP 3: Check if signature is EXPIRING SOON (non-blocking)
        // Trigger refresh after action so next action has fresh signature
        if (this.signatureManager.isSignatureExpiringSoon(file)) {
            // Save current view mode before initiating refresh
            this.viewModeBeforeRefresh = this.viewMode;
            this.isRefreshInitiatedByThis = true;
            this.refreshNeeded$.next();
        }
    }

    /**
     * Handle menu item clicks with signature validation
     * 
     * Flow:
     * 1. Check if refresh is in progress - if so, block all actions
     * 2. Check if ANY files in list have expired signatures (proactive)
     *    - YES: Emit refresh for all files first
     * 3. Check if signature of clicked file is EXPIRED (blocking)
     *    - YES: Queue refresh BEFORE action
     * 4. Execute action
     * 5. Check if signature is EXPIRING SOON (non-blocking)
     *    - YES: Queue refresh AFTER action
     */
    public onMenuClick(fileItem: AmsFilesListItemMenuClick): void {

        // Check if THIS action needs signature validation
        const needsSignature = SIGNATURE_DEPENDENT_MENU_ACTIONS.includes(fileItem.key);

        if (!needsSignature) {
            // Skip all signature checks - proceed directly
            this.menuClick.emit(fileItem);
            return;
        }
        // STEP 0: Check if refresh is in progress
        if (this.isRefreshing) {
            return;
        }

        const file = this.files.find((f: AmsFile) => f.id === fileItem.file.id);

        // STEP 1: Check if ANY files have expired signatures
        if (this.hasExpiredSignaturesInList()) { 
            
            // Store the pending action so listener can execute it when signatures refresh
            this.pendingMenuClick = fileItem;
            this.pendingFileId = file?.id;
            if (file?.id) {
                this.filesPendingRefresh.add(file.id);
            }
            
            // IMPORTANT: Also save to service so it survives component destruction
            if (file?.id) {
                this.pendingActionsSrv.setPendingAction(file.id, fileItem);
            }
            
            // Setup listener for THIS file's signature refresh
            // Only set up listener for signature-dependent actions
            this.setupPendingActionListener(fileItem, file?.id);

            // Save current view mode before initiating refresh
            this.viewModeBeforeRefresh = this.viewMode;
            this.isRefreshInitiatedByThis = true;
            this.refreshNeeded$.next();
            return;
        }

        if (!file) {
            return;
        }

        // STEP 2: Check if signature is EXPIRED (blocking)
        if (this.signatureManager.isSignatureExpired(file)) {
            // Save current view mode before initiating refresh
            this.viewModeBeforeRefresh = this.viewMode;
            this.isRefreshInitiatedByThis = true;
            
            // Store the action to execute after refresh
            this.pendingMenuClick = fileItem;
            this.pendingFileId = file.id;
            this.filesPendingRefresh.add(file.id);

            // IMPORTANT: Also save to service so it survives component destruction
            this.pendingActionsSrv.setPendingAction(file.id, fileItem);

            // NEW: Setup listener for THIS file's signature refresh
            // Only set up listener for signature-dependent actions
            this.setupPendingActionListener(fileItem, file.id);

            // Trigger debounced refresh - will consolidate multiple requests
            this.refreshNeeded$.next();
            return;
        }

        // STEP 3: Signature is valid, execute action
        // (Highlight file if it's a notes menu click - same as original)
        if (fileItem.key === 'notes') {
            this.fileSelected = (this.fileSelected?.id === fileItem.file.id) ? null : fileItem.file;
        }

        this.menuClick.emit(fileItem);

        // STEP 4: Check if signature is EXPIRING SOON (non-blocking)
        // Trigger refresh after action so next action has fresh signature
        if (this.signatureManager.isSignatureExpiringSoon(file)) {
            // Save current view mode before initiating refresh
            this.viewModeBeforeRefresh = this.viewMode;
            this.isRefreshInitiatedByThis = true;
            this.shouldTriggerRefreshAfterAction = true;
            this.refreshNeeded$.next();
        }
    }

    protected initGridItems(): void {
        this.displayedFiles = this.files.slice(0, this.GRID_ITEMS_PER_PAGE);
    }

    protected setMenus() {
        this.completeMenu = [...this.menu, this.itemMenuWithComments, this.itemMenuPdfWithSignatures];
        this.menuWithComments = [...this.menu, this.itemMenuWithComments];
        this.menuWithSignatures = [...this.menu, this.itemMenuPdfWithSignatures];
    }

    /**
     * Handle signature refresh needed signal from StorageGridPreviewComponent
     * 
     * This is triggered when the preview component receives a 403 Forbidden error
     * (signature expired or invalid). Forward this to Core service to refresh signatures.
     */
    public onSignatureRefreshNeeded(): void {
        this.coreFileSignatureService.refreshSignatures();
    }

    /**
     * Check if ANY of the files in the list have expired signatures
     */
    private hasExpiredSignaturesInList(): boolean {
        return this.files.some((f: AmsFile) => 
            this.signatureManager.isSignatureExpired(f)
        );
    }

    /**
     * Check files for expired signatures and emit refresh if needed
     * Called when user switches to grid view or loads more grid items
     */
    private checkFilesForExpiredSignatures(): void {
        const expiredCount = this.files.filter((f: AmsFile) => 
            this.signatureManager.isSignatureExpired(f)
        ).length;

        if (expiredCount > 0) {
            // Save current view mode before initiating refresh
            this.viewModeBeforeRefresh = this.viewMode;
            this.isRefreshInitiatedByThis = true;
            this.refreshNeeded$.next();
        }
    }

    /**
     * Setup a one-time listener for pending action execution
     * 
     * When a signature-dependent action is blocked due to expired signature,
     * this listener waits for the file's signature to be refreshed via CoreFileSignatureService.
     * Once refreshed, the pending action is automatically executed.
     * 
     * Only called for signature-dependent actions (SIGNATURE_DEPENDENT_MENU_ACTIONS).
     * 
     * @param fileItem The action that was blocked and will be executed when signature refreshes
     * @param fileId The ID of the file whose signature we're waiting for
     */
    private setupPendingActionListener(fileItem: AmsFilesListItemMenuClick, fileId: string): void {
        this.coreFileSignatureService.getSignatureUpdates$()
            .pipe(
                // Only listen for THIS file's signature updates
                filter(({ fileId: updatedFileId }) => updatedFileId === fileId),
                // Execute only once, then unsubscribe
                take(1),
                takeUntil(this.destroy$)
            )
            .subscribe(({ fileId, access }) => {
                // GUARD: ngOnChanges STEP 1.5 may have already executed and cleared this
                // pending action (e.g. it ran first off the same signature refresh that
                // triggered this subscription). Skip to avoid firing the action twice.
                if (this.pendingFileId !== fileId) {
                    return;
                }

                // Double-check signature is now valid
                const updatedFile = this.files?.find(f => f.id === fileId);
                if (updatedFile && !this.signatureManager.isSignatureExpired(updatedFile)) {
                    // Execute the pending action immediately
                    if (fileItem.key === 'preview') {
                        this.fileSelected = updatedFile;
                        this.actionClick.emit(updatedFile);
                    } else {
                        this.menuClick.emit(fileItem);
                    }

                    // Clear pending state
                    this.pendingMenuClick = null;
                    this.pendingFileId = null;
                } else {
                    // console.warn(`[SignedFilesListComponent] Signature still expired after refresh, won't execute action`);
                }
            });
    }

    /**
     * Cache signatures from files array into signature map
     * This allows updating signatures independently without re-rendering files
     * 
     * ALSO broadcasts signatures to all other SignedFilesListComponent instances
     * through CoreFileSignatureService so they can update their local caches
     * 
     * Called on every files input change to keep cache fresh
     */
    private cacheSignaturesFromFiles(files: Array<AmsFile>): void {
        if (!files) return;
        
        files.forEach((file: AmsFile) => {
            if (file.access) {
                this.fileSignaturesMap.set(file.id, file.access);
                
                // Broadcast to other components through Core service so they stay fresh too
                this.coreFileSignatureService.getSignatureUpdates$().subscribe(({ fileId, access }) => {
                    if (fileId === file.id) {
                        this.fileSignaturesMap.set(fileId, access);
                    }
                });
            }
        });
    }

    /**
     * Get cached signature for a file
     * Used by actions (download, preview) to access signatures without depending on files array
     * 
     * Lookup order:
     * 1. Check signature cache (fast, always fresh)
     * 2. Check current files array (fallback)
     * 3. Return null if not found
     */
    public getFileSignature(fileId: string): any {
        // First try the signature cache
        if (this.fileSignaturesMap.has(fileId)) {
            return this.fileSignaturesMap.get(fileId);
        }
        
        // Fallback to files array
        const file = this.files?.find((f: AmsFile) => f.id === fileId);
        return file?.access || null;
    }

    /**
     * TrackBy function for *ngFor loops over files
     * Prevents unnecessary re-creation of child components when files array updates
     * but individual files haven't changed (e.g., during signature refresh)
     * 
     * Critical for: pdf-annotations-viewer component (expensive to recreate)
     * 
     * @param index Array index
     * @param file File object
     * @returns Unique identifier for the file (its ID)
     */
    public trackByFileId(index: number, file: AmsFile): string {
        return file.id;
    }

    ngOnDestroy(): void {
        // IMPORTANT: Do NOT clear pending action state from component or service!
        // If a pending action exists, preserve it in the service so it survives component destruction
        // When a new component instance is created, it will restore and execute the pending action
        if (this.pendingMenuClick !== null && this.pendingFileId !== null) {
            // Action stays in service - DO NOT call clearPendingAction()
        }
        
        this.refreshNeeded$.complete();
        this.destroy$.next();
        this.destroy$.complete();
        this.fileSignaturesMap.clear();
    }


    /**
     * Handle notes saved event from core-pdf-annotations-viewer to reload counter after saving notes.
     */
    notesSaved(event: any) {
        // After saving a note, refresh the notes counter for this message
        this.annotationsSaved.emit();
    }

    
}
